import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { ratingsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Star, X, CheckCircle, Car, Truck, Key } from 'lucide-react';

const StarRating = ({ rating, onRatingChange, size = 'lg' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-7 h-7',
    lg: 'w-10 h-10'
  };
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onRatingChange(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className="transition-transform hover:scale-110 focus:outline-none"
          data-testid={`star-${star}`}
        >
          <Star
            className={`${sizeClasses[size]} transition-colors ${
              star <= (hoverRating || rating)
                ? 'fill-[#00BFA6] text-[#00BFA6]'
                : 'fill-slate-200 text-slate-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ServiceIcon = ({ type }) => {
  const icons = {
    ride: Car,
    delivery: Truck,
    rental: Key
  };
  const Icon = icons[type] || Car;
  
  return (
    <div className="w-16 h-16 rounded-2xl bg-[#00BFA6]/10 flex items-center justify-center mx-auto mb-4">
      <Icon className="w-8 h-8 text-[#00BFA6]" />
    </div>
  );
};

const serviceLabels = {
  ride: 'Viaje',
  delivery: 'Entrega',
  rental: 'Renta'
};

export const RatingModal = ({ 
  isOpen, 
  onClose, 
  serviceType, 
  serviceId, 
  ratedUserId,
  ratedUserName,
  canSkip = true,
  onSkip,
  onSuccess 
}) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Por favor selecciona una calificación');
      return;
    }

    setSubmitting(true);
    try {
      await ratingsAPI.create({
        service_type: serviceType,
        service_id: serviceId,
        rating,
        comment: comment.trim() || null,
        rated_user_id: ratedUserId
      });
      
      setSubmitted(true);
      toast.success('¡Gracias por tu calificación!');
      
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar calificación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    onSkip?.();
    onClose();
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="card-elevated w-full max-w-md animate-slide-up text-center p-8">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-slate-800 font-['Outfit'] mb-2">
            ¡Gracias!
          </h3>
          <p className="text-slate-500">Tu calificación nos ayuda a mejorar</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="card-elevated w-full max-w-md animate-slide-up">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-800 font-['Outfit']">
              Califica tu {serviceLabels[serviceType] || 'Servicio'}
            </h3>
            {canSkip && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSkip}
                className="text-slate-400 hover:text-slate-600"
                data-testid="skip-rating-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>

          {/* Service Icon */}
          <ServiceIcon type={serviceType} />

          {/* User being rated */}
          {ratedUserName && (
            <p className="text-center text-slate-600 mb-4">
              ¿Cómo fue tu experiencia con <span className="font-semibold text-slate-800">{ratedUserName}</span>?
            </p>
          )}

          {/* Star Rating */}
          <div className="flex justify-center mb-6">
            <StarRating rating={rating} onRatingChange={setRating} />
          </div>

          {/* Rating Labels */}
          <div className="flex justify-between text-xs text-slate-400 mb-6 px-2">
            <span>Malo</span>
            <span>Regular</span>
            <span>Bueno</span>
            <span>Muy bueno</span>
            <span>Excelente</span>
          </div>

          {/* Comment */}
          <div className="mb-6">
            <Textarea
              placeholder="Cuéntanos más sobre tu experiencia (opcional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400 rounded-xl resize-none h-24"
              data-testid="rating-comment"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {canSkip && (
              <Button
                variant="outline"
                className="flex-1 h-12 border-slate-200 text-slate-600 rounded-xl"
                onClick={handleSkip}
              >
                Más tarde
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={submitting || rating === 0}
              className={`${canSkip ? 'flex-1' : 'w-full'} h-12 btn-primary rounded-xl`}
              data-testid="submit-rating-btn"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
              ) : (
                'Enviar calificación'
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Star display component for showing ratings
export const StarDisplay = ({ rating, showValue = true, size = 'sm' }) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5'
  };

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClasses[size]} ${
            star <= Math.round(rating)
              ? 'fill-[#FFD166] text-[#FFD166]'
              : 'fill-slate-200 text-slate-200'
          }`}
        />
      ))}
      {showValue && (
        <span className="text-slate-600 text-sm ml-1">
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export default RatingModal;
